import { MapPinIcon, PencilIcon } from '@heroicons/react/20/solid';
import Moment from 'moment/moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { formatDate } from '../../../../common-utils/formatters';
import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type { CoproTableData } from '../../../@types/CoproTableData';
import type { RnicTableData } from '../../../@types/RnicTableData';
import {
  setCoprosFormData, setSelectedRow,
} from '../../../store/CoproReducer';
import { useAppDispatch } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import { getCopro } from '../../../utils/api';
import {
  COPRO_TYPE_FILTER_COMPANY_COPRO, COPRO_TYPE_FILTER_SUBCONTRACTED_COPRO,
  COPRO_TYPE_FILTER_SUBCONTRACTOR_FOR_COPRO,
  coproTypes,
} from '../../../utils/copros';
import { JWTHeader } from '../../../utils/JWTHeader';
import Badge from '../../common/Badge';
import CoproForm from '../../common/CoproForm';
import Map from '../../common/CoproMap';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup';
import RedirectionCoproButtons from '../../common/RedirectionCoproButtons';
import Spinner from '../../common/Spinner';

const headerKeys = {
  nonMatched2: 'ACTIONS',
  remoteId: 'N° IMMAT',
  name: 'NOM',
  nonMatched: 'ADRESSE',
  batchSize: 'NB LOTS HAB / LOCAL PRO',
  type: 'TYPE',
  termEndAt: 'FIN DE MANDAT',
};

const headers = [
  { title: 'ACTIONS', class: '' },
  { title: 'N° IMMAT', class: '' },
  { title: 'NOM', class: '' },
  { title: 'ADRESSE', class: '' },
  { title: 'NB LOTS HAB / LOCAL PRO', class: '' },
  { title: 'TYPE', class: '' },
  { title: 'DEBUT DE MANDAT', class: '' },
  { title: 'AG REGISTRE', class: '' },
  { title: 'FIN DE MANDAT', class: '' },
];

const keysNeeded = ['numeroDimmatriculation', 'nomDusageDeLaCopropriete', 'numeroEtVoieAdresseDeReference', 'codePostalAdresseDeReference', 'communeAdresseDeReference', 'nombreTotalDeLots', 'nombreDeLotsAUsageDhabitation', 'dateDeFinDuDernierMandat', 'identificationDuRepresentantLegalRaisonSocialeEtLeNume', 'matchedBanIdAdr'];
const finalNames = ['N° IMMAT', 'NOM', 'ADRESSE', 'CP', 'COMMUNE', 'NB LOTS', 'MIXTE HAB / PRO', 'DATE FIN MANDAT', 'NOM DU SYNDIC DANS REGISTRE', 'matchedBanIdAdr', 'mandatUnformatted'];

const sortableColumns = [
  'N° IMMAT', 'NOM', 'ADRESSE', 'NB LOTS HAB / LOCAL PRO', 'TYPE', 'DEBUT DE MANDAT', 'AG REGISTRE', 'FIN DE MANDAT',
];

function CoproManaging() {
  const dispatch = useAppDispatch();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const coprosFormData = useSelector((state: RootState) => state.copro.coprosFormData);
  const [copros, setCopros] = useState<Partial<CoproTableData>[]>(null);
  const [coprosRnic, setCoprosRnic] = useState(null);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editingCopro, setEditingCopro] = useState<number | null>(null);
  const [filteredData, setFilteredData] = useState<Partial<CoproTableData>[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number>(null);

  const openEditPopup = (copro: number) => {
    setEditingCopro(copro);
    setIsPopupOpen(true);
  };

  function parseDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day); // month is 0-based in JavaScript Date
  }

  function sortCoprosByTermEndAt(coprosData: Partial<CoproTableData>[]): Partial<CoproTableData>[] {
    const nonNullKeys = coprosData.filter((key) => key['FIN DE MANDAT'] && key['FIN DE MANDAT']!.trim() !== '');
    const nullKeys = coprosData.filter((key) => !key['FIN DE MANDAT'] || key['FIN DE MANDAT']!.trim() === '');

    nonNullKeys.sort((a, b) => parseDate(b['FIN DE MANDAT']!).getTime() - parseDate(a['FIN DE MANDAT']!).getTime());

    return [...nonNullKeys, ...nullKeys];
  }

  async function fetchCopros() {
    setIsPopupOpen(false);
    setStatus('PENDING');
    try {
      const response = await fetch(getCopro(company.id, null, null, 'termEndAt', 'desc'), JWTHeader(jwt));
      const result = await response.json();
      dispatch(setCoprosFormData(result.results.filter((copro) => copro.validatedByUser === true)));

      fetch(`/api/companies/${company.id}/rnics`, JWTHeader(jwt))
        .then((responseRnic) => responseRnic.json())
        .then((responseRnic) => {
          const coproImmatSet = new Set(result.results.map((copro) => copro.remoteId));

          const rnicData = responseRnic.filter(
            (rnic) => !coproImmatSet.has(rnic.numeroDimmatriculation),
          );

          interface RnicApiItem {
            [key: string]: string | number | boolean | null | undefined;
          }
          const formattedRnicData = rnicData.map((item: RnicApiItem) => {
            const newItem: Record<string, unknown> = {};
            keysNeeded.forEach((key, index) => {
              newItem[finalNames[index]] = item[key];
            });
            newItem.ADRESSEBAN = '';
            if (item.matchedBanNumero) {
              newItem.ADRESSEBAN = (newItem.ADRESSEBAN as string) + String(item.matchedBanNumero);
            }
            if (item.matchedBanRep) {
              newItem.ADRESSEBAN = (newItem.ADRESSEBAN as string) + String(item.matchedBanRep);
            }
            if (item.matchedBanNomVoie) {
              newItem.ADRESSEBAN = `${newItem.ADRESSEBAN as string  } ${String(item.matchedBanNomVoie)}`;
            }
            if (newItem['DATE FIN MANDAT']) {
              newItem.mandatUnformatted = newItem['DATE FIN MANDAT'];
              newItem['DATE FIN MANDAT'] = formatDate(String(newItem['DATE FIN MANDAT']));
            } else {
              newItem.mandatUnformatted = null;
              newItem['DATE FIN MANDAT'] = newItem['DATE FIN MANDAT'];
            }

            newItem.VALIDE = true;
            return newItem as unknown as RnicTableData;
          });

          setCoprosRnic(formattedRnicData);
          setStatus('SUCCESS');
        }).catch((error) => {
          console.error('Error fetching rnic copro data :', error);
          showToast('error', 'Une erreur est survenue lors du chargement des données.');
        });
    } catch (error) {
      console.error('Error fetching copros :', error);
      setStatus('ERROR');
      setErrorMessage('Erreur lors du chargement des copropriétés');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  }

  useEffect(() => {
    if (coprosFormData) {
      const registeredCopro = sortCoprosByTermEndAt(
        coprosFormData
          .map((copro) => {
            const newItem: Partial<CoproTableData> = {};

            Object.keys(copro).forEach((key) => {
              if (key === 'termEndAt' && copro[key]) {
                newItem[headerKeys[key]] = Moment(copro[key]).format('DD/MM/YYYY');
              } else if (Object.keys(headerKeys).includes(key)) {
                newItem[headerKeys[key]] = copro[key];
              }
            });

            newItem.TYPE = coproTypes[copro.type];

            const isSubcontracted = copro.company.id !== company.id && copro.company.subcontractors.some((subcontractor) => subcontractor.id === company.id);

            newItem.NOM = (
              <div className="flex items-center">
                <RedirectionCoproButtons coproId={copro.id} />
                {copro.name}
              </div>
            );

            // Add the actions column
            newItem.ACTIONS = (
              <div className="flex w-full items-center justify-center">
                <PencilIcon
                  className={`h-5 w-5 ${isSubcontracted ? 'cursor-not-allowed text-gray-500' : 'cursor-pointer text-gray-600 hover:text-gray-800'}`}
                  onClick={!isSubcontracted ? () => openEditPopup(copro.id) : null}
                />
              </div>
            );

            newItem['Type de copropriété'] = copro.company.id === company.id ? COPRO_TYPE_FILTER_COMPANY_COPRO
              : company.subcontractors.some((subcontractor) => subcontractor.id === copro.company.id) ? COPRO_TYPE_FILTER_SUBCONTRACTED_COPRO
                : company.subcontractorFor.some((subcontractorForData) => subcontractorForData.id === copro.company.id) && COPRO_TYPE_FILTER_SUBCONTRACTOR_FOR_COPRO;

            newItem.id = copro.id;

            if (copro.addresses.length === 0) {
              newItem.ADRESSE = '';
            } else {
              newItem.ADRESSE = (
                <div className="flex items-center">
                  <div className="flex h-8 w-8 cursor-pointer items-center justify-center">
                    <MapPinIcon
                      className="mr-2 h-5 w-5 text-gray-600 hover:text-gray-800"
                      onClick={() => {
                        const mapCopros = document.getElementById('map-copros');
                        const mainScrollable = document.getElementById('mainScrollable');
                        if (mapCopros && mainScrollable) {
                          const mapCoprosRect = mapCopros.getBoundingClientRect();
                          const scrollTop = mainScrollable.scrollTop + mapCoprosRect.top - 85;
                          mainScrollable.scrollTo({
                            top: scrollTop,
                            behavior: 'smooth',
                          });
                        }
                        setSelectedLocationId(copro.id);
                      }}
                    />
                  </div>
                  {`${copro.addresses[0].address} ${copro.addresses[0].postalCode} ${copro.addresses[0].city}`}
                </div>
              );
            }
            newItem.generalMeetingApiDate = copro.generalMeetingApiDate;
            newItem.generalMeetingDate = copro.generalMeetingDate;

            // if the the date de fin de mandat is in the API override the date set by the termEndAt from RNIC file import as the API is more reliable
            if (copro.details && copro.details.detailsInforattachementDatefinmandat) {
              newItem['FIN DE MANDAT'] = Moment(copro.details.detailsInforattachementDatefinmandat).format('DD/MM/YYYY');
            }

            if (copro.details && copro.details.detailsDonneesfinancieresDateag && copro.details && copro.details.detailsDonneesfinancieresDateag) {
              newItem['AG REGISTRE'] = Moment(copro.details.detailsDonneesfinancieresDateag).format('DD/MM/YYYY');
            } else {
              newItem['AG REGISTRE'] = '';
            }
            if (copro.details && copro.details.detailsInforattachementDatedebutmandat) {
              newItem['DEBUT DE MANDAT'] = Moment(copro.details.detailsInforattachementDatedebutmandat).format('DD/MM/YYYY');
            } else {
              newItem['DEBUT DE MANDAT'] = '';
            }

            return newItem;
          }),
      );
      setCopros(registeredCopro);
      const selectedIndices = registeredCopro
        .map((item, index) => (item.VALIDE ? index : -1))
        .filter((index) => index !== -1);
      dispatch(setSelectedRow({ step: 'coproValidation', rows: selectedIndices }));
    }
  }, [coprosFormData]);

  useEffect(() => {
    if (company && company.id && jwt) {
      fetchCopros();
    }
  }, [company, jwt]);

  return (
    <div className="flex flex-col">
      <div className="mb-8 overflow-x-hidden rounded-lg bg-white p-4 shadow-md">
        <div className="mt-4 mb-12 text-lg">
          <p>
            Cette page permet d'avoir une vue globale de vos copropriétés. La première partie de la page contient la liste de toutes les nouvelles copropriétés enregistrées dans le
            Registre National d'Immatriculation des Copropriétés (RNIC) avec votre SIREN (s'il y en a).
            La seconde partie de la page vous permet de consulter la liste de toutes vos copropriétés enregistrées sur la plateforme Déclarations Juridiques ainsi que leur géolocalisation.
          </p>
        </div>
        {status === 'SUCCESS' && (
          <div className="my-10">
            {coprosRnic && coprosRnic.length === 0
              && (
                <div className="mx-10 my-5 text-center">
                  <Badge
                    mode="warning"
                    text="Aucune nouvelle copropriété détectée"
                  />
                </div>
              )}
            <h2 className="mt-12 mb-6 text-center text-xl text-rgpd-blue-light">VOS COPROPRIÉTÉS</h2>
            <div className="grid grid-cols-2 gap-10 lg:grid-cols-3">
              <div className="col-span-2 flex flex-col justify-start">
                <MUITable
                  data={copros}
                  headers={headers}
                  tableClass="min-w-full bg-white border text-sm"
                  sortableColumns={sortableColumns}
                  filterColumnName={new Set(copros.map((copro) => copro['Type de copropriété'])).size > 1 ? 'Type de copropriété' : null}
                  isSearchable
                  setParentFilteredData={setFilteredData}
                />
                {copros && copros.length === 0 && (
                  <div className="mx-10 my-10 text-center">
                    <Badge mode="warning" text="Aucune copropriété enregistrée" />
                  </div>
                )}
              </div>
              <div className="col-span-2 flex h-[80dvh] min-h-80 flex-col pt-16 lg:col-span-1">
                <Map coproIds={filteredData.map((data) => data.id)} selectedLocationIdProp={selectedLocationId} />
              </div>
            </div>
            <Popup
              isOpen={isPopupOpen}
              onClose={() => {
                setIsPopupOpen(false);
              }}
              title={editingCopro ? 'Modifier la copro' : 'Ajouter une nouvelle copro'}
              additionalClass="h-4/6 w-6/12"
              children={(
                <CoproForm
                  initialDataId={editingCopro}
                  setIsPopupOpen={setIsPopupOpen}
                  companyId={company.id}
                />
              )}
            />
          </div>
        )}
        {status === 'ERROR' && (
          <div className="mx-10 my-32 text-center">
            <Badge mode="error" text={errorMessage} />
          </div>
        )}
        {status === 'ERROR' && (
          <div className="mx-10 my-32 text-center">
            <Badge mode="error" text={errorMessage} />
          </div>
        )}
        {status === 'PENDING' && (
          <p className="mx-10 my-32 text-center">
            <Spinner aria-label="Center-aligned spinner" size="xl" />
          </p>
        )}
      </div>
    </div>
  );
}

export default CoproManaging;
